/**
 * DEMO ONLY — in-memory store for the dynamic pricing slabs demo.
 * useReducer + Context. Optionally persists to localStorage so a live demo
 * survives a page refresh. No backend, no Redux.
 */
import React, { createContext, useContext, useEffect, useReducer } from "react";
import {
  INITIAL_STATE,
  DEFAULT_GLOBAL_BANDS,
  SPLIT_GLOBAL_BANDS,
  SAMPLE_PRICES,
  BAND_PRESETS,
} from "./demoData";

const STORAGE_KEY = "pbdemo.v1";

const DemoContext = createContext(null);

function loadInitial() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  return INITIAL_STATE;
}

function reducer(state, action) {
  switch (action.type) {
    case "SET_GLOBAL_BANDS":
      return { ...state, globalBands: action.bands };

    case "APPLY_SPLIT":
      return { ...state, globalBands: SPLIT_GLOBAL_BANDS };

    case "RESET_GLOBAL_BANDS":
      return { ...state, globalBands: DEFAULT_GLOBAL_BANDS };

    case "UPSERT_PRICE": {
      const price = action.price;
      const exists = state.prices.some((p) => p.id === price.id);
      return {
        ...state,
        prices: exists
          ? state.prices.map((p) => (p.id === price.id ? price : p))
          : [...state.prices, { ...price, id: nextId(state.prices) }],
      };
    }

    case "TOGGLE_PRICE_STATUS":
      return {
        ...state,
        prices: state.prices.map((p) =>
          p.id === action.id
            ? { ...p, status: p.status === "active" ? "inactive" : "active" }
            : p
        ),
      };

    case "SET_SHOW_INACTIVE":
      return { ...state, showInactive: action.value };

    case "RESET_ALL":
      return {
        globalBands: DEFAULT_GLOBAL_BANDS,
        prices: SAMPLE_PRICES,
        showInactive: false,
      };

    default:
      return state;
  }
}

function nextId(prices) {
  return prices.reduce((max, p) => Math.max(max, p.id), 0) + 1;
}

export function DemoProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* ignore */
    }
  }, [state]);

  return (
    <DemoContext.Provider value={{ state, dispatch }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used inside <DemoProvider>");
  return ctx;
}

/** Resolve the band set that applies to a price row (preset or global). */
export function bandsForPrice(price, globalBands) {
  if (price && price.presetName && BAND_PRESETS[price.presetName]) {
    return BAND_PRESETS[price.presetName];
  }
  return globalBands;
}
